import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, Hammer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = diffSec / 60;
  if (diffMin < 60) return `${Math.floor(diffMin)}m ago`;
  const diffH = diffMin / 60;
  if (diffH < 24) return `${Math.floor(diffH)}h ago`;
  const diffD = diffH / 24;
  if (diffD < 7) return `${Math.floor(diffD)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function HeroCard({ project, onChange }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!project) {
    return (
      <div
        data-testid="hero-empty"
        className="tactile-card p-7 text-center bg-chalk border-2 border-dashed border-masking"
      >
        <div className="mx-auto w-16 h-16 rounded-2xl bg-timber-900 flex items-center justify-center shadow-tactile">
          <img src="/logo.svg" alt="" className="w-12 h-12" draggable={false} />
        </div>
        <h2 className="font-heading text-2xl font-bold mt-4 text-timber-900 leading-tight">
          Plan your first Half Job
        </h2>
        <p className="text-timber-700 mt-2 text-sm leading-relaxed">
          {`Snap a few photos and Half Job's AI copilot will break the work down into steps, flag what you need, and help you finish what you started.`}
        </p>
        <Button
          data-testid="hero-start-cta"
          onClick={() => navigate("/projects/new")}
          className="mt-6 bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[56px] px-8 text-base font-bold btn-press"
        >
          <Hammer size={20} weight="duotone" className="mr-2" /> Plan a job
        </Button>
      </div>
    );
  }

  const step = project.current_step;
  const cover = fileUrl(project.cover_photo_id);

  const doneStep = async () => {
    if (!step) return;
    try {
      setBusy(true);
      await api.patch(`/projects/steps/${step.id}`, { status: "done" });
      toast.success("Nice. Step done.");
      onChange?.();
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="hero-card"
      className="bg-chalk border-2 border-denim rounded-2xl shadow-tactile-lg overflow-hidden"
    >
      {cover && (
        <div className="h-32 w-full bg-kraft overflow-hidden border-b-2 border-denim">
          <img src={cover} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-5">
        <p className="label-cap">Pick up where you left off</p>
        <h2
          className="font-heading text-2xl font-bold text-timber-900 mt-1 leading-tight"
          data-testid="hero-title"
        >
          {project.title}
        </h2>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          {project.location && (
            <p className="text-sm text-timber-700">{project.location}</p>
          )}
          <span className="text-timber-500">·</span>
          <p className="text-sm text-timber-700 font-mono" data-testid="hero-progress-line">
            {project.steps_done}/{project.step_count} steps
            {project.last_worked_at && ` · ${relativeTime(project.last_worked_at)}`}
          </p>
        </div>

        {step ? (
          <div className="mt-4 bg-kraft border-2 border-dashed border-masking rounded-xl p-4">
            <p className="label-cap text-masking">Next action</p>
            <p
              className="font-heading text-xl font-bold text-timber-900 mt-1 leading-snug"
              data-testid="hero-next-step"
            >
              {step.title}
            </p>
            {step.notes && (
              <p className="text-sm text-timber-700 mt-1">{step.notes}</p>
            )}
          </div>
        ) : (
          <p className="mt-4 text-timber-700">All steps done. Tap to wrap up.</p>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3">
          <Button
            data-testid="hero-open-project"
            variant="outline"
            onClick={() => navigate(`/projects/${project.id}`)}
            className="border-2 border-denim text-denim min-h-[56px] font-bold"
          >
            Open <ArrowRight size={18} className="ml-1" />
          </Button>
          <Button
            data-testid="hero-done-step"
            onClick={doneStep}
            disabled={!step || busy}
            className="bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[56px] font-bold disabled:opacity-50"
          >
            <CheckCircle size={20} weight="bold" className="mr-1" /> Done
          </Button>
        </div>
      </div>
    </div>
  );
}
